import {
  RUTERO_BOOKING_LIST_TABS,
  RuteroBookingListSort,
  RuteroBookingListTab,
  RuteroBookingTabCounts,
} from '@bandido/shared/types';

/**
 * La FORMA de la pantalla del listado: qué pestañas hay, qué columnas se
 * dibujan y qué dice cada vacío.
 *
 * Las cuatro pestañas son cuatro TRABAJOS y su vocabulario es el del backend
 * (`RUTERO_BOOKING_LIST_TABS`), no una copia: el `Record` exhaustivo de acá no
 * compila si el contrato suma una pestaña sin rótulo, y los contadores llegan
 * con la MISMA identidad (`meta.tabCounts`), así que el badge no puede quedar
 * apuntando a una pestaña que no existe (§21.22).
 */

export interface BookingListTabSpec {
  tab: RuteroBookingListTab;
  labelKey: string;
  /**
   * La pestaña que se cuenta en el encabezado. Es UNA: un badge en cada una
   * dejaría de significar "esto necesita una mano hoy".
   */
  badge: boolean;
}

const TAB_SPECS: Record<RuteroBookingListTab, BookingListTabSpec> = {
  attention: {
    tab: 'attention',
    labelKey: 'travelServices.bookingList.tabs.attention',
    badge: true,
  },
  upcoming: {
    tab: 'upcoming',
    labelKey: 'travelServices.bookingList.tabs.upcoming',
    badge: false,
  },
  all: {
    tab: 'all',
    labelKey: 'travelServices.bookingList.tabs.all',
    badge: false,
  },
  closed: {
    tab: 'closed',
    labelKey: 'travelServices.bookingList.tabs.closed',
    badge: false,
  },
};

/** En el orden del contrato: trabajo urgente, operación, todo, historial. */
export const BOOKING_LIST_TABS: BookingListTabSpec[] =
  RUTERO_BOOKING_LIST_TABS.map((tab) => TAB_SPECS[tab]);

/**
 * El número del badge, o nada. Sale del contador de la respuesta —el mismo
 * where que la pestaña— y DESAPARECE en cero: un badge gris con un 0 no es una
 * cola vacía, es ruido (el vacío celebratorio de la pestaña ya lo dice).
 */
export function bookingListTabBadge(
  spec: BookingListTabSpec,
  counts?: RuteroBookingTabCounts,
): number | undefined {
  if (!spec.badge) return undefined;
  const count = counts?.[spec.tab];
  return count && count > 0 ? count : undefined;
}

/** Navegación con flechas entre pestañas, ciclando en las puntas. */
export function bookingListAdjacentTab(
  tab: RuteroBookingListTab,
  direction: 1 | -1,
): RuteroBookingListTab {
  const index = BOOKING_LIST_TABS.findIndex((spec) => spec.tab === tab);
  const next =
    (index + direction + BOOKING_LIST_TABS.length) % BOOKING_LIST_TABS.length;
  return BOOKING_LIST_TABS[next].tab;
}

export type BookingListColumnId =
  | 'state'
  | 'service'
  | 'holder'
  | 'provider'
  | 'cancellationPenalty'
  | 'milestone'
  | 'total'
  | 'case'
  | 'created'
  | 'reconfirmation'
  | 'action';

export interface BookingListColumn {
  id: BookingListColumnId;
  labelKey: string;
  align: 'left' | 'right';
  /**
   * Ancho de la columna en px. La tabla es DENSA: sin un ancho declarado, un
   * chip de estado largo (el de una operación estancada es una frase entera)
   * se lleva media pantalla y deja el servicio en una columna de dos letras.
   */
  width: number;
  /** Con qué columna del endpoint se ordena, donde la pestaña lo permite. */
  sort?: RuteroBookingListSort;
}

/**
 * Las columnas, en UNA definición: el encabezado y las celdas se dibujan
 * recorriendo esta misma lista, así que no puede quedar un título sin celda ni
 * una celda sin título (§21.9). El orden es el de lectura de la fila: en qué
 * estado está, qué es, de quién, con quién, qué sigue, cuánto, dónde se
 * caso comercial, cuándo se reservó, qué hacer.
 */
export const BOOKING_LIST_COLUMNS: BookingListColumn[] = [
  {
    id: 'state',
    labelKey: 'travelServices.bookingList.columns.state',
    align: 'left',
    width: 175,
  },
  {
    id: 'service',
    labelKey: 'travelServices.bookingList.columns.service',
    align: 'left',
    width: 205,
    sort: 'serviceDate',
  },
  {
    id: 'holder',
    labelKey: 'travelServices.bookingList.columns.holder',
    align: 'left',
    width: 115,
  },
  {
    id: 'provider',
    labelKey: 'travelServices.bookingList.columns.provider',
    align: 'left',
    width: 155,
  },
  {
    id: 'milestone',
    labelKey: 'travelServices.bookingList.columns.milestone',
    align: 'left',
    width: 145,
  },
  { id: 'cancellationPenalty', labelKey: 'travelServices.bookingList.columns.cancellationPenalty', align: 'left', width: 190, sort: 'cancellationPenaltyStartsAt' },
  {
    id: 'total',
    labelKey: 'travelServices.bookingList.columns.total',
    align: 'right',
    width: 110,
    sort: 'finalAmount',
  },
  {
    id: 'case',
    labelKey: 'travelServices.bookingList.columns.case',
    align: 'left',
    width: 155,
  },
  {
    id: 'created',
    labelKey: 'travelServices.bookingList.columns.created',
    align: 'left',
    width: 115,
    sort: 'createdAt',
  },
  { id: 'reconfirmation', labelKey: 'travelServices.bookingList.columns.reconfirmation', align: 'left', width: 165 },
  {
    id: 'action',
    labelKey: 'travelServices.bookingList.columns.action',
    align: 'right',
    width: 100,
  },
];

/**
 * Lo que mide la tabla entera. Debajo de eso la tabla scrollea DENTRO de su
 * caja y nunca la página (mismo criterio que la grilla de eSIMs).
 */
export const BOOKING_LIST_TABLE_WIDTH = BOOKING_LIST_COLUMNS.reduce(
  (total, column) => total + column.width,
  0,
);

/**
 * Dónde se puede reordenar. En "atención" y "próximas" el orden ES el trabajo
 * —urgencia y día del servicio—: ofrecer ordenar por plata ahí desarmaría la
 * cola que la pestaña existe para dar.
 */
export function bookingListTabSupportsSort(tab: RuteroBookingListTab): boolean {
  return tab === 'all' || tab === 'closed';
}

/** La cola se mira entera: el backend no la pagina y la pantalla tampoco. */
export function bookingListTabPaginates(tab: RuteroBookingListTab): boolean {
  return tab !== 'attention';
}

/** "Próximas a viajar" es la única que se lee agrupada por día. */
export function bookingListTabGroupsByDay(tab: RuteroBookingListTab): boolean {
  return tab === 'upcoming';
}

/**
 * El vacío de cada pestaña.
 *
 * "Nada necesita atención" es el MEJOR estado del sistema y se celebra; el
 * resto ofrece la salida que corresponde: ampliar el rango cuando la pestaña
 * es una ventana de tiempo, y limpiar los filtros cuando fueron los filtros
 * los que dejaron la lista vacía. Un "sin resultados" genérico no dice ninguna
 * de las tres cosas.
 */
export interface BookingListEmptyState {
  tone: 'success' | 'info';
  titleKey: string;
  bodyKey: string;
  /** El vacío que ofrece deshacer lo que lo causó. */
  action?: 'clearFilters';
}

export function bookingListEmptyState({
  tab,
  hasFilters,
}: {
  tab: RuteroBookingListTab;
  hasFilters: boolean;
}): BookingListEmptyState {
  if (tab === 'attention' && !hasFilters) {
    return {
      tone: 'success',
      titleKey: 'travelServices.bookingList.empty.attention.title',
      bodyKey: 'travelServices.bookingList.empty.attention.body',
    };
  }
  if (hasFilters) {
    return {
      tone: 'info',
      titleKey: 'travelServices.bookingList.empty.filtered.title',
      bodyKey: 'travelServices.bookingList.empty.filtered.body',
      action: 'clearFilters',
    };
  }
  if (tab === 'upcoming') {
    return {
      tone: 'info',
      titleKey: 'travelServices.bookingList.empty.upcoming.title',
      bodyKey: 'travelServices.bookingList.empty.upcoming.body',
    };
  }
  return {
    tone: 'info',
    titleKey: 'travelServices.bookingList.empty.default.title',
    bodyKey: 'travelServices.bookingList.empty.default.body',
  };
}
